import { defineStore } from 'pinia'
import { Recap, RecapResponse } from '../types/recap'
import { LanguageChoice } from '../i18n'
import { ref, Ref } from 'vue'
import { DatabaseRecapDefinition, RecapDefinitionsModel } from '../models/RecapDefinitionsModel'
import { RecapResponsesModel } from '../models/RecapReponsesModel'
import { RecapImageUpload } from '../models/RecapImagesModel'
import RecapDefinition from '../components/recaps/RecapDefinition.vue'
import { Http } from '@capacitor-community/http'
import { useUserStore } from './useUserStore'
import { hash, logInfo } from '../utils'
import { captureException } from '@sentry/vue'
import { Nullish } from '../types/utility'

export const useRecapStore = defineStore<'recap', RecapStore>('recap', () => {
  const recaps = ref<Recaps>({})

  const recapImages = ref<RecapImages>([])

  const resetStoreState = () => {
    for (const key in recaps) {
      delete recaps.value[key]
    }
    recap_responses.value = []
  }

  const recap_responses = ref<RecapResponse[]>([])
  const { writeRecapResponses } = RecapResponsesModel()
  const saveRecapResponses = async (responses?: RecapResponse[]) => {
    if (responses && responses.length) {
      return await writeRecapResponses(responses)
    }
    return await writeRecapResponses(recap_responses.value)
  }
  function setRecapsFromSync(syncRecaps: any, initialLanguage: LanguageChoice) {
    const keys = Object.keys(syncRecaps.recaps)
    keys.forEach((key: any) => {
      const recap = syncRecaps.recaps[key]
      const taskMappings = syncRecaps.task_mappings[key]
      if (recap && taskMappings && taskMappings.length) {
        taskMappings.forEach((task_id: string) => {
          recaps.value[`${task_id}-${recap.recap_definition.id}`] = {
            recapDefinition: recap.recap_definition,
            task_id: task_id,
            language: initialLanguage,
          }
        })
      }
    })
  }

  // Grab single recap
  const setRecapFromDatabase = async (recap_id: string, task_id: string) => {
    const def = await RecapDefinitionsModel().getRecapDefinition(recap_id)
    const lang = useUserStore().language
    if (def && def.length) {
      // Force the definition to load, even if it's already in the store
      recaps.value[`${task_id}-${recap_id}`] = {
        recapDefinition: JSON.parse(def[0].recap_definition) as Recap,
        task_id,
        language: lang,
      }
    } else {
      console.trace()
      console.error({ task_id, recap_id })
      captureException('Recap not found')
    }
  }
  // Remove recap
  const removeRecapFromStore = (
    recap_id: number | string | Nullish,
    task_id: number | string | Nullish
  ) => {
    delete recaps.value[`${task_id}-${recap_id}`]
  }

  function setRecapsFromDatabase(
    databaseRecaps: DatabaseRecapDefinition[],
    initialLanguage: LanguageChoice
  ) {
    databaseRecaps.forEach((recap) => {
      const definition = JSON.parse(recap.recap_definition) as Recap
      const taskIds = recap.task_ids.split(',')
      taskIds.forEach((task_id) => {
        recaps.value[`${task_id}-${recap.recap_id}`] = {
          recapDefinition: definition,
          task_id,
          language: initialLanguage,
        }
      })
    })
  }
  async function setResponses(
    responses: RecapResponse[],
    options: { write?: boolean } = { write: true }
  ) {
    recap_responses.value = responses
    if (options.write) {
      await saveRecapResponses()
    }
  }
  /**
   * Fetches a recap definition. This method should only be used for testing recaps.
   */
  async function fetchRecap(id: string, task_id: string) {
    let origin = ''
    if (
      /https?:\/\/app\.neptune-(acceptance|staging|testing|qa|production)/.test(
        window.location.origin
      )
    ) {
      origin = window.location.origin.replace(/:\/\/app\./, '://global.')
    }

    const response = await Http.post({
      url: `${origin}/api/v1/recap_definition/show_with_params`,
      data: { id, task_id },
    }).catch(() => ({ data: null }))

    // The 0 here is a placeholder task ID so we don't accidentally overwrite data for a real recap.
    recaps.value[`0-${id}`] = {
      recapDefinition: response.data,
      task_id,
      language: 'en_US',
    }
  }

  const saveRecap = async (recapDefinition: typeof RecapDefinition, taskID: string) => {
    const response = await (recapDefinition as any).getRecapResponse()
    if (response) {
      if (response && 'validationError' in response) {
        // Validation error, not a runtime error
        logInfo('debug', 'saveTaskRecap', `Validation error saving response for task ${taskID}`, {
          taskID,
        })
        return { validationError: true }
      }
      if (!response.task_id) {
        // Something went wrong, log to Sentry and prevent saving
        logInfo('error', 'saveTaskRecap', 'No task ID found in response', { response })
        return false
      }
      const existingIndex = recap_responses.value?.findIndex((res) => res.task_id === taskID)
      if (existingIndex !== null && existingIndex > -1) {
        recap_responses.value.splice(existingIndex, 1, response)
      } else {
        recap_responses.value.push(response)
      }
      const saved = await saveRecapResponses([response])
      return saved
    }
    return false
  }

  async function getRecapHashes(): Promise<{ [key: string]: string }> {
    const recaps = await RecapDefinitionsModel().getRecapDefinitionHashes()
    const mappedRecaps: { [key: string]: string } = {}
    for (const recap of recaps) {
      mappedRecaps[recap.recap_id] = recap.hash
    }
    return mappedRecaps
  }

  async function getRecapTaskMappingHashes(): Promise<{ [key: string]: string }> {
    const recaps = await RecapDefinitionsModel().getAllRecapDefinitions()
    const mappedTaskMappings: { [key: string]: string } = {}
    for (const recap of recaps) {
      mappedTaskMappings[recap.recap_id] = hash(recap.task_ids.split(','))
    }
    return mappedTaskMappings
  }

  return {
    recaps,
    recapMappings: ref<RecapMappings>({}),
    recapImages,
    recap_responses,
    setRecapsFromSync,
    setRecapsFromDatabase,
    setRecapFromDatabase,
    removeRecapFromStore,
    setResponses,
    fetchRecap,
    storeId: ref('0'), // Added for type compatibility with the venue Recap Store via Pinia
    resetStoreState,
    saveRecap,
    getRecapHashes,
    getRecapTaskMappingHashes,
  }
})

type Recaps = {
  [key: string]: {
    recapDefinition: Recap | null
    task_id: string | null
    language: LanguageChoice
  }
}

type RecapMappings = {
  [key: string]: string[]
}

type RecapImages = { task_id: string; recap_id: string; image: RecapImageUpload }[]

type RecapStoreFunctions = {
  setRecapsFromSync: (syncRecaps: any, initialLanguage: LanguageChoice) => void
  setRecapsFromDatabase: (
    databaseRecaps: DatabaseRecapDefinition[],
    initialLanguage: LanguageChoice
  ) => void
  setRecapFromDatabase: (recap_id: string, task_id: string) => Promise<void>
  removeRecapFromStore: (
    recap_id: number | string | Nullish,
    task_id: number | string | Nullish
  ) => void
  setResponses: (responses: RecapResponse[], options?: { write?: boolean }) => Promise<void>
  fetchRecap: (id: string, task_id: string) => Promise<void>
  resetStoreState: () => void
  saveRecap: (
    recapDefinition: typeof RecapDefinition,
    taskID: string
  ) => Promise<{ validationError: boolean } | boolean>
  getRecapHashes: () => Promise<{ [key: string]: string }>
  getRecapTaskMappingHashes: () => Promise<{ [key: string]: string }>
}

type RecapStoreData = {
  recaps: Ref<Recaps>
  recapMappings: Ref<RecapMappings>
  recapImages: Ref<RecapImages>
  recap_responses: Ref<RecapResponse[]>
}

export type RecapStore = RecapStoreData & RecapStoreFunctions

export type RecapStoreRefs = RecapStoreData
